import omit from 'lodash/omit';
import uniqBy from 'lodash/uniqBy';
import {
  CrmIntegrationType,
  CrmSyncStatus,
  CustomFormRecordStatus,
  ObjectRecord,
  VeevaVaultObjectMetadataLayouts,
} from '@alucio/aws-beacon-amplify/src/models';
import { SYNC_ENTRY_FETCH_TYPE, SYNC_ENTRY_REQUEST_TYPE, SYNC_ENTRY_STATUS, SyncEntry } from '../../ISyncManifest';
import {
  VEEVA_VAULT_TABLES,
  VeevaVaultFieldStatus,
  VeevaVaultItemType,
  VeevaVaultObjectFieldSource,
  VeevaVaultObjectField,
  VeevaVaultObjectFieldType,
  VeevaVaultObjectLayout,
  VeevaVaultObjectMetadata,
  VeevaVaultSectionLayoutItem,
  VeevaVaultSectionType,
  VeevaVaultPicklists,
  VeevaVaultUpsertObjectPayload,
  VeevaVaultUpsertObjectsOutput,
  VeevaVaultResponseStatus,
  VeevaVaultFormSetting,
  SPECIAL_FIELDS,
  VeevaVaultUpsertRecord,
  VeevaVaultObjectToDeletePayload,
  VeevaVaultDeleteObjectsPayload,
  VeevaVaultDeleteObjectsOutput,
} from '../../CRMVeevaVaultTypes';
import * as logger from 'src/utils/logger';
import keyBy from 'lodash/keyBy';
import {
  CRMAccount,
  CRMSubmitMeetingPayload,
  CRMSubmitResponseReferences,
  RecordSubmitResponse,
  SYNC_STATUS,
} from '../../CRMIndexedDBTypes';
import { SPECIAL_FIELD_HANDLER, handleLayoutControlSection } from './VeevaVaultControlHandler';
import { AttendeesAccounts } from '../../Translators/VeevaFormTranslator';
import {
  buildUpsertObjectRecord,
  getObjectAPINameFromFieldKey,
  handleAttendeeObjectsPayloadsCreation,
  handleChildObjectsPayloadsCreation, handleCustomFormObjectsPayloadsCreation,
} from '../../Translators/VeevaVault/VeevaVaultTranslatorUtils';

type FieldsToIgnore = Record<string, string[]>

// THESE ARE FIELDS THAT ARE HANDLED ON SUBMIT OR ARE SYSTEM HANDLED
export const VEEVA_VAULT_LOOKUPS_TO_IGNORE: FieldsToIgnore = {
  [VEEVA_VAULT_TABLES.CALL]: ['account__v', 'ownerid__v'],
  [VEEVA_VAULT_TABLES.MEDICAL_DISCUSSION]: ['interaction__v'],
  [VEEVA_VAULT_TABLES.CALL_DETAIL]: ['call2__v'],
  [VEEVA_VAULT_TABLES.CALL_DISCUSSION]: ['call2__v'],
}

export const VEEVA_VAULT_PICKLISTS_TO_IGNORE: FieldsToIgnore = {
  [VEEVA_VAULT_TABLES.CALL]: ['call2_status__v'],
}

export const VEEVA_VAULT_FIELDS_TO_IGNORE_FOR_SUBMISSION: FieldsToIgnore = {
  [VEEVA_VAULT_TABLES.CALL]: [SPECIAL_FIELDS.USERS_ATTENDEES],
  [VEEVA_VAULT_TABLES.MEDICAL_INQUIRY]: ['disclaimer__v'],
};

//* FORM METADATA SYNCING UTILITIES *//
export function getStructureObjectLayouts(
  syncEntry: SyncEntry,
  response: VeevaVaultObjectMetadataLayouts): SyncEntry[] {
  const objectMetadata: VeevaVaultObjectMetadata = JSON.parse(response.metadata);
  const objectLayouts: VeevaVaultObjectLayout[] = JSON.parse(response.layouts);
  logger.veevaVaultSyncer.debug(`Structuring ${syncEntry.parameters.apiName} layouts/metadata based on response...`,
    { objectMetadata, objectLayouts });

  // SINCE THE RESPONSE ALREADY CONTAINS THE DIFFERENT AVAILABLE LAYOUTS FOR THE OBJECT,
  // WE WILL CREATE ONE "SYNCED" SYNCENTRY PER LAYOUT
  const syncEntries = objectLayouts.map<SyncEntry>((layout: VeevaVaultObjectLayout) => ({
    ...syncEntry,
    id: `${objectMetadata.name}-OBJECT_TYPE-${layout.object_type}`,
    status: SYNC_ENTRY_STATUS.SYNCED,
    subType: SYNC_ENTRY_REQUEST_TYPE.RECORD_TYPE,
    lastSynced: new Date().getTime(),
    parameters: { ...syncEntry.parameters, recordTypeId: layout.object_type },
    data: {
      objectInfo: objectMetadata,
      layout,
    },
  }))

  return [{
    // THIS ENTRY WILL BE IGNORED AS IT'S BEEN RECREATED WITH ITS OBJECT TYPE
    ...syncEntry,
    bypassError: true,
    status: SYNC_ENTRY_STATUS.BYPASSED_ERROR,
  }, ...syncEntries];
}

// BASED ON THE LAYOUTS, THIS FUNCTION WILL CREATE NEW SYNCENTRIES ACCORDING TO THE FORM'S NECESSITIES (PICKLISTS, LOOKUPS...)
export function getAdditionalEntriesBasedOnLayouts(entries: SyncEntry[]): SyncEntry[] {
  logger.veevaVaultSyncer.info('Getting additional entries based on the layouts information...');
  const picklistsIndex: {[object: string]: { [objectType: string]: Set<string> }} = {};
  const lookupsIndex: {[object: string]: Set<string>} = {};
  const newEntries: SyncEntry[] = [];

  for (const entry of entries) {
    const layout: VeevaVaultObjectLayout | undefined = entry.data?.layout;
    const objectMetadata: VeevaVaultObjectMetadata | undefined = entry.data?.objectInfo;
    const isLayoutEntry = entry.subType === SYNC_ENTRY_REQUEST_TYPE.RECORD_TYPE && !!layout;

    if (!isLayoutEntry || !objectMetadata) {
      continue;
    }

    const indexedFields = keyBy(objectMetadata.fields, 'name');

    for (const section of layout.sections || []) {
      // A "DETAIL" TYPE OF SECTION IS A SECTION ORIENTED TO FIELDS
      if (section.type === VeevaVaultSectionType.DETAIL) {
        const items = section.properties?.items ?? [];

        for (const item of items) {
          const fieldMetadata: VeevaVaultObjectField = indexedFields[item.reference || ''];
          const isValidAndModifiable = isValidAndModifiableField(item, fieldMetadata);

          if (isValidAndModifiable) {
            const specialFieldHandler = SPECIAL_FIELD_HANDLER[objectMetadata.name]?.[item.reference || ''];

            if (specialFieldHandler) {
              logger.veevaVaultSyncer.info(`Found special field handler for [${item.reference}]...`);
              const additionalEntriesForSpecialField = specialFieldHandler(objectMetadata, fieldMetadata);
              newEntries.push(...additionalEntriesForSpecialField);
              continue;
            }

            switch (fieldMetadata?.type) {
              case VeevaVaultObjectFieldType.PICKLIST: {
                const isIgnoredField =
                  VEEVA_VAULT_PICKLISTS_TO_IGNORE[objectMetadata.name]?.includes(item.reference || '');
                if (!isIgnoredField && !!fieldMetadata.picklist) {
                  if (!picklistsIndex[objectMetadata.name]) {
                    picklistsIndex[objectMetadata.name] = {};
                  }
                  if (!picklistsIndex[objectMetadata.name][layout.object_type]) {
                    picklistsIndex[objectMetadata.name][layout.object_type] = new Set<string>();
                  }
                  picklistsIndex[objectMetadata.name][layout.object_type].add(fieldMetadata.picklist);
                }
                break;
              }

              case VeevaVaultObjectFieldType.OBJECT: {
                const isIgnoredField =
                  VEEVA_VAULT_LOOKUPS_TO_IGNORE[objectMetadata.name]?.includes(item.reference || '');
                if (!isIgnoredField && fieldMetadata?.object?.name) {
                  if (!lookupsIndex[objectMetadata.name]) {
                    lookupsIndex[objectMetadata.name] = new Set<string>();
                  }
                  lookupsIndex[objectMetadata.name].add(fieldMetadata.object.name);
                }
                break;
              }
            }
          }
        }
        // A "CONTROL" TYPE OF SECTION IS A SECTION ORIENTED TO OTHER KIND OF COMPONENTS, LIKE "ZVODS"
      } else if (section.type === VeevaVaultSectionType.CONTROL) {
        const additionalEntriesForControlSection = handleLayoutControlSection(section, objectMetadata);
        newEntries.push(...additionalEntriesForControlSection);
      }
    }
  }

  Object.keys(picklistsIndex).forEach((objectName) => {
    Object.keys(picklistsIndex[objectName]).forEach((objectType) => {
      newEntries.push({
        url: '',
        type: CrmIntegrationType.VEEVA_VAULT,
        fetchType: SYNC_ENTRY_FETCH_TYPE.REGULAR,
        subType: SYNC_ENTRY_REQUEST_TYPE.PICKLIST,
        status: SYNC_ENTRY_STATUS.PENDING,
        lastSynced: new Date().getTime(),
        id: objectName + '-PICKLISTS-' + objectType,
        parameters: {
          fieldNames: Array.from(picklistsIndex[objectName][objectType] ?? []),
          apiName: objectName,
          recordTypeId: objectType,
          parentId: objectName,
        },
      });
    })
  });

  Object.keys(lookupsIndex).forEach((objectName) => {
    lookupsIndex[objectName].forEach((lookup) => {
      newEntries.push({
        url: '',
        type: CrmIntegrationType.VEEVA_VAULT,
        fetchType: SYNC_ENTRY_FETCH_TYPE.REGULAR,
        subType: SYNC_ENTRY_REQUEST_TYPE.LOOKUP,
        status: SYNC_ENTRY_STATUS.PENDING,
        lastSynced: new Date().getTime(),
        id: objectName + '-LOOKUPS-' + lookup,
        parameters: {
          lookupApiName: lookup,
          apiName: objectName,
          parentId: objectName,
        },
      })
    });
  });

  return uniqBy(newEntries, 'id');
}

function isValidAndModifiableField(item: VeevaVaultSectionLayoutItem, fieldMetadata?: VeevaVaultObjectField): boolean {
  const isAField = item.type === VeevaVaultItemType.FIELD;
  const isModifiable = !item.read_only && !!fieldMetadata?.editable;
  const isActive = item.status === VeevaVaultFieldStatus.ACTIVE;
  const isSystemManagedField = fieldMetadata?.source === VeevaVaultObjectFieldSource.SYSTEM;

  return isAField && isModifiable && isActive && !isSystemManagedField;
}

export function processAccountsResponse(accounts: any[]): CRMAccount[] {
  return accounts.map((account: {[key: string]: any}) => ({
    ...omit(account, ['address__vr']),
    id: account.id,
    addresses: account?.address__vr?.data?.map((address: {[key: string]: any}) => address) || [],
  }));
}

export function structureVeevaVaultFormSettingsPicklists(
  layout: VeevaVaultObjectLayout,
  objectMetadata: VeevaVaultObjectMetadata,
  picklistSyncEntry?: SyncEntry): VeevaVaultPicklists {
  logger.veevaVaultSyncer.info(`Structuring [${objectMetadata.name}] picklists for VeevaVaultFormSettings...`);

  const formattedPicklists: VeevaVaultPicklists = {};
  const indexedPickListFields = objectMetadata.fields.reduce((acc, field) => {
    if (field.type === VeevaVaultObjectFieldType.PICKLIST && field.picklist) {
      const isInLayout = layout.sections?.some((section) =>
        section.properties?.items?.some((item) => item.reference === field.name))

      if (isInLayout) {
        acc[field.picklist] = [...(acc[field.picklist] || []), field];
      }
    }
    return acc;
  }, {} as { [picklistName: string]: VeevaVaultObjectField[] });

  Object.keys(picklistSyncEntry?.data || {}).forEach((picklistName) => {
    (indexedPickListFields[picklistName] || []).forEach((picklistField) => {
      formattedPicklists[picklistField.name] = {
        picklistValues: picklistSyncEntry?.data[picklistName].picklistValues,
      };

      // CHECK IF IT DEPENDS ON OTHER PICKLIST
      if (picklistField.controlling_picklist) {
        formattedPicklists[picklistField.name].controllingPicklistName = picklistField.controlling_picklist;
        formattedPicklists[picklistField.name].picklistDependencies =
          picklistField.picklist_dependencies?.picklistDependencies;
      }
    });
  });

  return formattedPicklists;
}
//* END METADATA SYNCING UTILITIES *//

//* SUBMISSIONS UTILITIES *//
export type UpsertObjectsResult = {
  objects: VeevaVaultUpsertObjectPayload[]
  response: CRMSubmitResponseReferences
}

export async function handleMainObjectSubmission(
  crmSubmitPayload: CRMSubmitMeetingPayload,
  mainFormSetting: VeevaVaultFormSetting,
  attendeesMapped: AttendeesAccounts,
  upsertVeevaVaultObjects: (apiName: string, payloads: VeevaVaultUpsertObjectPayload[])
    => Promise<CRMSubmitResponseReferences>,
): Promise<UpsertObjectsResult> {
  logger.veevaVaultSyncer.info('Handling creation of the main object...');
  const payload = await buildUpsertObjectRecord({
    attendeesMapped,
    objectFormSetting: mainFormSetting,
    crmValues: crmSubmitPayload.mainCrmValues,
    crmSubmitPayload,
    referenceId: 'main-record',
    objectType: mainFormSetting.objectType,
  });

  let response: CRMSubmitResponseReferences = {};

  try {
    response = await upsertVeevaVaultObjects(mainFormSetting.apiName, [payload]);
  } catch (e) {
    logger.veevaVaultSyncer.error('Failed to create main record in Veeva Vault', e);
    throw new ErrorEvent('CRM Error', { error: getErrorMessagesFromFailedRequest(e)[0] });
  }

  return {
    objects: [payload],
    response,
  };
}

export async function handleAttendeesObjectsSubmission(
  crmSubmitPayload: CRMSubmitMeetingPayload,
  mainFormSetting: VeevaVaultFormSetting,
  mainObjectVeevaVaultId: string,
  mainObject: VeevaVaultUpsertRecord,
  attendees: AttendeesAccounts,
  upsertVeevaVaultObjects: (apiName: string, payloads: VeevaVaultUpsertObjectPayload[]) =>
    Promise<CRMSubmitResponseReferences>,
): Promise<UpsertObjectsResult> {
  logger.veevaVaultSyncer.info('Handling attendees objects submission...');
  const attendeesPayloads = handleAttendeeObjectsPayloadsCreation(
    crmSubmitPayload, mainFormSetting, mainObjectVeevaVaultId, mainObject, attendees);
  let response: CRMSubmitResponseReferences = {};

  try {
    response = await upsertVeevaVaultObjects(mainFormSetting.apiName, attendeesPayloads);
  } catch (e) {
    logger.veevaVaultSyncer.error('Failed to create attendee records in Veeva Vault', e);
    response = handleUnhandledAPIError(e);
  }

  return {
    objects: attendeesPayloads,
    response,
  };
}

// WHEN SUBMITTING AN ALREADY EXISTING MAIN CALL, THIS FUNCTION IS IN CHARGE
// OF UPDATING THE EXISTING CALLS AND CREATING NEW ONES IN CASE THERE ARE NEW ATTENDEES
export async function handleUpsertMainObjects(
  crmSubmitPayload: CRMSubmitMeetingPayload,
  mainFormSetting: VeevaVaultFormSetting,
  attendeesMapped: AttendeesAccounts,
  upsertVeevaVaultObjects: (apiName: string, payloads: VeevaVaultUpsertObjectPayload[], idName?: string)
    => Promise<CRMSubmitResponseReferences>,
): Promise<UpsertObjectsResult> {
  logger.veevaVaultSyncer.info('Handling main objects submission...');
  const mainObjectVeevaVaultId = crmSubmitPayload.mainCrmRecordId;

  if (!mainObjectVeevaVaultId) {
    logger.veevaVaultSyncer.error('Existing crm record id not found...');
    return { objects: [], response: {} };
  }

  const mainObjectPayload = await buildUpsertObjectRecord({
    attendeesMapped,
    objectFormSetting: mainFormSetting,
    crmValues: { ...crmSubmitPayload.mainCrmValues, id: mainObjectVeevaVaultId },
    crmSubmitPayload,
    referenceId: 'main-record',
    isUpdate: true,
  });

  const attendeesPayloads = handleAttendeeObjectsPayloadsCreation(
    crmSubmitPayload, mainFormSetting, mainObjectVeevaVaultId, mainObjectPayload.object, attendeesMapped);

  const payloads = [...attendeesPayloads, mainObjectPayload];
  let response: CRMSubmitResponseReferences = {};

  try {
    response = await upsertVeevaVaultObjects(mainFormSetting.apiName, payloads, 'id');
  } catch (e) {
    logger.veevaVaultSyncer.error('Failed to create main records in Veeva Vault', e);
    response = handleUnhandledAPIError(e);
  }

  return {
    response,
    objects: payloads,
  };
}

export async function handleChildObjectsSubmission(
  attendeesMapped: AttendeesAccounts,
  crmSubmitPayload: CRMSubmitMeetingPayload,
  mainObjects: UpsertObjectsResult,
  upsertVeevaVaultObjects: (apiName: string, payloads: VeevaVaultUpsertObjectPayload[], idName?: string)
    => Promise<CRMSubmitResponseReferences>,
): Promise<UpsertObjectsResult> {
  logger.veevaVaultSyncer.info('Handling child objects submission...');
  const childObjectsPayload =
    await handleChildObjectsPayloadsCreation(crmSubmitPayload, mainObjects, attendeesMapped);

  const groupedObjectsRequests: { [apiName: string]: VeevaVaultUpsertObjectPayload[] } = {};

  childObjectsPayload.forEach((object: VeevaVaultUpsertObjectPayload) => {
    (groupedObjectsRequests[object.objectAPIName] ??= []).push(object);
  });

  const results = await Promise.allSettled(
    Object.keys(groupedObjectsRequests).map((apiName) =>
      upsertVeevaVaultObjects(apiName, groupedObjectsRequests[apiName], 'id')),
  );

  const groupedResponses: CRMSubmitResponseReferences =
    groupPromisesResponse(results, 'Failed to create child records in Veeva Vault');

  return { objects: childObjectsPayload, response: groupedResponses };
}

export async function handleObjectsDeletion(
  crmSubmitPayload: CRMSubmitMeetingPayload,
  childObjectsUpsertResponse: CRMSubmitResponseReferences,
  mainObjectAPIName: string,
  deleteVeevaVaultObjects: (objectAPIName: string, payloads: VeevaVaultObjectToDeletePayload[])
    => Promise<CRMSubmitResponseReferences>,
): Promise<CRMSubmitResponseReferences> {
  logger.veevaVaultSyncer.info('Handling objects deletion...');
  const payloadsToDelete = getObjectsToDelete(crmSubmitPayload, childObjectsUpsertResponse, mainObjectAPIName);

  const results = await Promise.allSettled(
    Object.keys(payloadsToDelete).map((apiName) =>
      deleteVeevaVaultObjects(apiName, payloadsToDelete[apiName])));

  return groupPromisesResponse(results, 'Failed to delete records in Veeva Vault');
}

export async function handleUpsertStandaloneFormRecordsFromMeeting(
  crmSubmitPayload: CRMSubmitMeetingPayload,
  mainObjectVeevaVaultId: string,
  upsertVeevaVaultObjects: (apiName: string, payloads: VeevaVaultUpsertObjectPayload[], dName?: string) =>
    Promise<CRMSubmitResponseReferences>,
): Promise<CRMSubmitResponseReferences> {
  logger.veevaVaultSyncer.info('Handling standalone form records submission...');

  const payloads: VeevaVaultUpsertObjectPayload[] =
    await handleCustomFormObjectsPayloadsCreation(crmSubmitPayload, mainObjectVeevaVaultId);

  const groupedObjectsRequests: { [apiName: string]: VeevaVaultUpsertObjectPayload[] } = {};

  payloads.forEach((object: VeevaVaultUpsertObjectPayload) => {
    (groupedObjectsRequests[object.objectAPIName] ??= []).push(object);
  });

  const results = await Promise.allSettled(
    Object.keys(groupedObjectsRequests).map((apiName) =>
      upsertVeevaVaultObjects(apiName, groupedObjectsRequests[apiName], 'id')),
  );

  return groupPromisesResponse(results, 'Failed to upsert the standalone forms...');
}

export async function handleDeleteStandaloneFormRecords(
  crmSubmitPayload: CRMSubmitMeetingPayload,
  deleteVeevaVaultObjects: (objectAPIName: string, payloads: VeevaVaultObjectToDeletePayload[])
    => Promise<CRMSubmitResponseReferences>,
): Promise<CRMSubmitResponseReferences> {
  logger.veevaVaultSyncer.info('Handling standalone form records deletion...');

  const payloadsToDelete: VeevaVaultDeleteObjectsPayload = {};

  for (const recordCustomForm of crmSubmitPayload.crmCustomFormRecords) {
    // ONLY DELETE RECORDS THAT HAVE BEEN SYNCED BUT ARE NOW MARKED AS DELETED
    if (recordCustomForm.model.status === CustomFormRecordStatus.DELETED &&
        recordCustomForm.model.crmFields?.externalId &&
        recordCustomForm.model.crmFields?.syncStatus === CrmSyncStatus.SYNCED) {
      const recordAPIName = recordCustomForm.model.crmFields.apiName;
      const formSetting = crmSubmitPayload.veevaVaultFormSettings.find((formSetting) =>
        formSetting.apiName === recordAPIName);

      if (formSetting) {
        const apiName = formSetting.apiName;
        (payloadsToDelete[apiName] ??= []).push({
          externalId: recordCustomForm.model.crmFields.externalId,
          beaconId: recordCustomForm.model.id,
        });
      }
    }
  }

  const results = await Promise.allSettled(
    Object.keys(payloadsToDelete).map((apiName) =>
      deleteVeevaVaultObjects(apiName, payloadsToDelete[apiName])),
  );

  return groupPromisesResponse(results, 'Failed to delete standalone form records...');
}

// CHECKS FOR A REMOVED ATTENDEE/CHILD RECORD
function getObjectsToDelete(
  crmSubmitPayload: CRMSubmitMeetingPayload,
  childObjectsUpsertResponse: CRMSubmitResponseReferences,
  mainObjectAPIName: string,
): VeevaVaultDeleteObjectsPayload {
  const objectsToDelete: VeevaVaultDeleteObjectsPayload = {};

  const addToDeletePayload = (apiName: string, record: { externalId: string; beaconId: string }) => {
    (objectsToDelete[apiName] ??= []).push(record);
  };

  const checkChildObject = (objectRecord: ObjectRecord, objectAPIName: string): void => {
    if (objectRecord.externalId && objectRecord.syncStatus === CrmSyncStatus.SYNCED) {
      const isObjectStillPresent = Object.values(childObjectsUpsertResponse).some((responseItem) =>
        responseItem.externalId === objectRecord.externalId);

      if (!isObjectStillPresent) {
        addToDeletePayload(getObjectAPINameFromFieldKey(objectAPIName), {
          externalId: objectRecord.externalId!,
          beaconId: objectRecord.id,
        });
      }
    }
  };

  // CHECKS THE PRIMARY ATTENDEE CHILD OBJECTS
  crmSubmitPayload.meetingORM.model.crmRecord?.crmCustomValues.forEach((value) => {
    value.objectRecords?.forEach((objectRecord) => {
      checkChildObject(objectRecord, value.fieldId);
    });
  });

  // CHECKS THE ADDITIONAL ATTENDEES AND THEIR CHILD OBJECTS
  for (const attendee of crmSubmitPayload.meetingORM.model.attendees) {
    const formAttendee =
      crmSubmitPayload.formValuesAttendees.find((formAttendee) => formAttendee.id === attendee.id);
    const shouldAttendeeBeDeleted = !formAttendee && attendee.crmRecord?.crmCallId &&
      attendee.crmRecord.crmSyncStatus === CrmSyncStatus.SYNCED

    if (shouldAttendeeBeDeleted) {
      addToDeletePayload(mainObjectAPIName, { externalId: attendee.crmRecord.crmCallId, beaconId: attendee.id });
    } else if (formAttendee) {
      attendee.crmRecord?.crmCustomValues.forEach((value) => {
        value.objectRecords?.forEach((objectRecord) => {
          checkChildObject(objectRecord, value.fieldId);
        });
      });
    }
  }

  return objectsToDelete;
}

export function structureUpsertResponse(
  payloads: VeevaVaultUpsertObjectPayload[],
  response: VeevaVaultUpsertObjectsOutput): CRMSubmitResponseReferences {
  const structuredResponse: CRMSubmitResponseReferences = payloads.reduce((acc, payload, index) => {
    const responseItem = response.data?.[index];
    const errorMessages = responseItem?.errors?.map(({ message }) => message) || [];
    const isErrorStatus = responseItem?.responseStatus === VeevaVaultResponseStatus.FAILURE ||
      responseItem?.responseStatus === VeevaVaultResponseStatus.EXCEPTION
    const syncStatus = isErrorStatus ? SYNC_STATUS.ERROR : SYNC_STATUS.SYNCED;
    const externalId = responseItem?.data?.id;

    // According to Veeva's Documentation:
    // "EXCEPTION - The API request could not be processed because of system error."
    if (!errorMessages.length && responseItem?.responseStatus === VeevaVaultResponseStatus.EXCEPTION) {
      errorMessages.push('Submission failed due to a Veeva Vault error.');
    }

    return {
      ...acc,
      [payload.referenceId]: {
        syncStatus,
        errorMessages,
        externalId,
      } satisfies RecordSubmitResponse,
    };
  }, {} as CRMSubmitResponseReferences);

  logger.veevaVaultSyncer.info('Structured upsert response: ', structuredResponse);
  return structuredResponse;
}

export function getErrorMessagesFromFailedRequest(e: any): string[] {
  const errorMessages: string[] = [];
  if (Array.isArray(e.errors)) {
    e.errors.forEach((error: any) => {
      if (typeof error.message === 'string') {
        errorMessages.push(error.message);
      }
    });
  }

  return errorMessages;
}

export function handleUnhandledAPIError(e: any): CRMSubmitResponseReferences {
  return {
    error: {
      syncStatus: SYNC_STATUS.ERROR,
      errorMessages: getErrorMessagesFromFailedRequest(e),
    },
  };
}

function groupPromisesResponse(
  results: PromiseSettledResult<CRMSubmitResponseReferences>[],
  errorLogMessage: string): CRMSubmitResponseReferences {
  let groupedResponses: CRMSubmitResponseReferences = {};

  for (const result of results) {
    if (result.status === 'fulfilled') {
      groupedResponses = { ...groupedResponses, ...result.value };
    } else {
      logger.veevaVaultSyncer.error(errorLogMessage, result.reason);
      const errors = Array.isArray(result.reason?.errors) ? result.reason.errors : [];
      const messages = errors.map((error => error.message));
      groupedResponses.error = {
        syncStatus: SYNC_STATUS.ERROR,
        errorMessages: messages.length ? messages : [''],
      };
    }
  }

  return groupedResponses;
}

export function structureDeleteObjectsResponse(
  payloads: VeevaVaultObjectToDeletePayload[],
  response: VeevaVaultDeleteObjectsOutput[]): CRMSubmitResponseReferences {
  const structuredResponse: CRMSubmitResponseReferences = payloads.reduce((acc, payload, index) => {
    const responseItem = response[index];
    const errorMessage = responseItem?.reason || '';
    const shouldIgnoreFailure = errorMessage === `MALFORMED_URL: The resource [${payload.externalId}] does not exist`;
    const syncStatus = responseItem?.status === 'rejected' &&
    !shouldIgnoreFailure ? SYNC_STATUS.ERROR : SYNC_STATUS.DELETED;

    return {
      ...acc,
      [payload.beaconId]: {
        syncStatus,
        errorMessages: errorMessage ? [errorMessage] : undefined,
        externalId: payload.externalId,
      } satisfies RecordSubmitResponse,
    };
  }, {} as CRMSubmitResponseReferences);

  logger.veevaVaultSyncer.info('Structured delete response: ', structuredResponse);
  return structuredResponse;
}
